import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { conversationsApi } from '../lib/api-client';
import { useAuth } from '../contexts/AuthContext';
import { MessageSquare, Calendar, User, TrendingUp } from 'lucide-react';
import type { Conversation } from '../types';

export default function Conversations() {
  const { organization } = useAuth();
  const [selectedStatus, setSelectedStatus] = useState<string>('all');

  const { data: conversationsData, isLoading } = useQuery({
    queryKey: ['conversations', organization?.id, selectedStatus],
    queryFn: () =>
      conversationsApi.list({
        organizationId: organization!.id,
        status: selectedStatus === 'all' ? undefined : selectedStatus,
      }),
    enabled: !!organization,
  });

  const conversations = conversationsData?.data?.data || [];

  const statusFilters = [
    { value: 'all', label: 'All Conversations' },
    { value: 'open', label: 'Open' },
    { value: 'closed', label: 'Closed' },
    { value: 'archived', label: 'Archived' },
  ];

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'open':
        return 'bg-green-100 text-green-700';
      case 'closed':
        return 'bg-gray-100 text-gray-700';
      case 'archived':
        return 'bg-blue-100 text-blue-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  const getSentimentColor = (score?: number) => {
    if (!score) return 'text-gray-500';
    if (score > 0.5) return 'text-green-600';
    if (score < -0.5) return 'text-red-600';
    return 'text-yellow-600';
  };

  if (isLoading) {
    return <div className="text-center py-12">Loading...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Conversations</h1>
          <p className="text-gray-500 mt-2">View and manage customer conversations</p>
        </div>
      </div>

      <div className="flex items-center space-x-2">
        {statusFilters.map((filter) => (
          <button
            key={filter.value}
            onClick={() => setSelectedStatus(filter.value)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              selectedStatus === filter.value
                ? 'bg-blue-600 text-white'
                : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-200'
            }`}
          >
            {filter.label}
          </button>
        ))}
      </div>

      {conversations.length === 0 ? (
        <div className="card p-12 text-center">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <MessageSquare className="w-8 h-8 text-gray-400" />
          </div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">No conversations yet</h3>
          <p className="text-gray-500">
            Conversations will appear here once visitors start chatting with your widgets
          </p>
        </div>
      ) : (
        <div className="card divide-y divide-gray-200">
          {conversations.map((conversation: Conversation) => (
            <div
              key={conversation.id}
              className="p-6 hover:bg-gray-50 transition-colors cursor-pointer"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                      <User className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">
                        {conversation.visitorName || conversation.visitorEmail || 'Anonymous Visitor'}
                      </h3>
                      {conversation.visitorEmail && conversation.visitorName && (
                        <p className="text-sm text-gray-500">{conversation.visitorEmail}</p>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
                    <div className="flex items-center space-x-2 text-sm">
                      <Calendar className="w-4 h-4 text-gray-400" />
                      <span className="text-gray-600">
                        {new Date(conversation.startedAt).toLocaleDateString()}
                      </span>
                    </div>

                    <div className="flex items-center space-x-2 text-sm">
                      <MessageSquare className="w-4 h-4 text-gray-400" />
                      <span className="text-gray-600">
                        {conversation.messageCount || 0} messages
                      </span>
                    </div>

                    {conversation.sentimentScore !== undefined && (
                      <div className="flex items-center space-x-2 text-sm">
                        <TrendingUp className="w-4 h-4 text-gray-400" />
                        <span className={getSentimentColor(conversation.sentimentScore)}>
                          Sentiment: {conversation.sentimentScore.toFixed(2)}
                        </span>
                      </div>
                    )}

                    {conversation.converted && (
                      <div className="flex items-center">
                        <span className="px-3 py-1 text-xs font-medium bg-green-100 text-green-700 rounded-full">
                          Converted
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                <div className="ml-4">
                  <span className={`px-3 py-1 text-xs font-medium rounded-full ${getStatusColor(conversation.status)}`}>
                    {conversation.status}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
